import {useIsTeamAdmin} from '@wandb/weave/common/hooks/useIsTeamAdmin';
import {
  useCanAccessBilling,
  useOrgName,
} from '@wandb/weave/common/hooks/useOrganization';
import {useViewerInfo} from '@wandb/weave/common/hooks/useViewerInfo';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {Dispatch, SetStateAction, useMemo, useState} from 'react';

import {
  useCanAccessInference,
  useInferenceContext,
} from '../../../inference/util';
import {CallChat} from '../../CallPage/CallChat';
import {Choice, Message, ToolCall} from '../../ChatView/types';
import {Empty} from '../../common/Empty';
import {
  EMPTY_PROPS_NO_LLM_PROVIDERS,
  EMPTY_PROPS_NO_LLM_PROVIDERS_ADMIN,
} from '../../common/EmptyContent';
import {TraceObjSchemaForBaseObjectClass} from '../../wfReactInterface/objectClassQuery';
import {TraceCallSchema} from '../../wfReactInterface/traceServerClientTypes';
import {PlaygroundContext} from '../PlaygroundContext';
import {PlaygroundMessageRole, PlaygroundState} from '../types';
import {ProviderStatus} from '../useConfiguredProviders';
import {InferenceBanner} from './InferenceBanner';
import {useLLMDropdownOptions} from './LLMDropdown';
import {PlaygroundCallStats} from './PlaygroundCallStats';
import {PlaygroundChatInput} from './PlaygroundChatInput';
import {PlaygroundChatTopBar} from './PlaygroundChatTopBar';
import {ProviderConfigDrawer} from './ProviderConfigDrawer';
import {useChatCompletionFunctions} from './useChatCompletionFunctions';
import {
  SetPlaygroundStateFieldFunctionType,
  useChatFunctions,
} from './useChatFunctions';

const EmptyWithSettingsButton: React.FC<{
  entity: string;
  project: string;
  isTeamAdmin: boolean;
  onConfigureProvider: () => void;
}> = ({entity, project, isTeamAdmin, onConfigureProvider}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Get the organization name for the billing URL
  const {orgName} = useOrgName({entityName: entity});
  // Check if user can access billing
  const {canAccessBilling} = useCanAccessBilling({entityName: entity});

  const emptyProps = isTeamAdmin
    ? EMPTY_PROPS_NO_LLM_PROVIDERS_ADMIN
    : EMPTY_PROPS_NO_LLM_PROVIDERS;

  return (
    <>
      <div className="flex flex-col items-center gap-[16px]">
        <Empty {...emptyProps} />
        <div className="flex gap-[8px]">
          {canAccessBilling ? (
            <TargetBlank href={`/account-settings/${orgName}/billing`}>
              <Button variant="secondary" icon="inference" size="medium">
                Activate W&B Inference
              </Button>
            </TargetBlank>
          ) : (
            <Tooltip
              content="Contact your billing administrator to activate Inference"
              trigger={
                <Button
                  variant="secondary"
                  icon="inference"
                  size="medium"
                  disabled>
                  Activate W&B Inference
                </Button>
              }
            />
          )}
          {isTeamAdmin ? (
            <Button
              variant="primary"
              onClick={() => setIsDrawerOpen(true)}
              icon="key-admin"
              size="medium">
              Configure provider
            </Button>
          ) : (
            <Tooltip
              content="Contact your team administrator to add an AI provider"
              trigger={
                <Button
                  variant="primary"
                  icon="key-admin"
                  size="medium"
                  disabled>
                  Configure provider
                </Button>
              }
            />
          )}
        </div>
      </div>
      <ProviderConfigDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          onConfigureProvider();
          setIsDrawerOpen(false);
        }}
        entity={entity}
      />
    </>
  );
};

// Check for unanswered tool calls using same logic as MessageList
const checkForUnansweredToolCalls = (
  messages: Message[],
  choices?: Choice[]
): number => {
  // Combine messages and choice messages for checking
  const allMessages = [...messages];

  // Add choice messages to the end of the message list
  if (choices && choices.length > 0) {
    choices.forEach(choice => {
      if (choice.message) {
        allMessages.push(choice.message);
      }
    });
  }

  let unansweredCount = 0;

  for (let i = 0; i < allMessages.length; i++) {
    const message = allMessages[i];

    // If there are no tool calls, continue to next message
    if (!('tool_calls' in message)) {
      continue;
    }

    // Get all the next messages where role = tool, these are all the responses
    const toolMessages: Message[] = [];
    let j = i + 1;
    while (j < allMessages.length && allMessages[j].role === 'tool') {
      toolMessages.push(allMessages[j]);
      j++;
    }

    if (message.tool_calls) {
      for (const toolCall of message.tool_calls as ToolCall[]) {
        const hasResponse = toolMessages.some(
          toolMessage => toolMessage.tool_call_id === toolCall.id
        );
        if (!hasResponse) {
          unansweredCount++;
        }
      }
    }
  }
  return unansweredCount;
};

export type PlaygroundChatProps = {
  entity: string;
  project: string;
  setPlaygroundStates: Dispatch<SetStateAction<PlaygroundState[]>>;
  playgroundStates: PlaygroundState[];
  setPlaygroundStateField: SetPlaygroundStateFieldFunctionType;
  setSettingsTab: (callIndex: number | null) => void;
  settingsTab: number | null;
  isOpenInPlayground?: boolean;
  customProvidersResult: TraceObjSchemaForBaseObjectClass<'Provider'>[];
  customProviderModelsResult: TraceObjSchemaForBaseObjectClass<'ProviderModel'>[];
  areCustomProvidersLoading: boolean;
  refetchCustomLLMs: () => void;
  refetchConfiguredProviders: () => void;
  configuredProvidersLoading: boolean;
  configuredProviders: Record<string, ProviderStatus>;
  savedModelsResult: TraceObjSchemaForBaseObjectClass<'LLMStructuredCompletionModel'>[];
  savedModelsLoading: boolean;
  showMarkdownInChatView: boolean;
  setShowMarkdownInChatView: (showMarkdownInChatView: boolean) => void;
};

export const PlaygroundChat = ({
  entity,
  project,
  setPlaygroundStates,
  playgroundStates,
  setPlaygroundStateField,
  setSettingsTab,
  settingsTab,
  isOpenInPlayground = false,
  customProvidersResult,
  customProviderModelsResult,
  areCustomProvidersLoading,
  refetchCustomLLMs,
  refetchConfiguredProviders,
  configuredProvidersLoading,
  configuredProviders,
  savedModelsResult,
  savedModelsLoading,
  showMarkdownInChatView,
  setShowMarkdownInChatView,
}: PlaygroundChatProps) => {
  const [chatText, setChatText] = useState('');

  // Check for unanswered tool calls across all playground states
  const disableSendMessage = useMemo(() => {
    let totalUnansweredCalls = 0;

    for (const state of playgroundStates) {
      const messages = state.traceCall?.inputs?.messages as
        | Message[]
        | undefined;
      const choices = (state.traceCall?.output as any)?.choices as
        | Choice[]
        | undefined;

      if (messages) {
        const unansweredCount = checkForUnansweredToolCalls(messages, choices);
        totalUnansweredCalls += unansweredCount;
      }
    }

    if (totalUnansweredCalls > 0) {
      return totalUnansweredCalls === 1
        ? 'Cannot send message while there is 1 unanswered tool call'
        : `Cannot send message while there are ${totalUnansweredCalls} unanswered tool calls`;
    }
    return null;
  }, [playgroundStates]);

  const {handleRetryStream, handleStreamSend} = useChatCompletionFunctions(
    setPlaygroundStates,
    setPlaygroundStateField,
    playgroundStates,
    entity,
    project,
    setChatText
  );

  const {deleteMessage, editMessage, deleteChoice, editChoice, addMessage} =
    useChatFunctions(setPlaygroundStateField);

  const handleAddMessage = (role: PlaygroundMessageRole, text: string) => {
    for (let i = 0; i < playgroundStates.length; i++) {
      addMessage(i, {role, content: text});
    }
    setChatText('');
  };

  const {userInfo} = useViewerInfo();
  const {isAdmin: maybeTeamAdmin} = useIsTeamAdmin(
    entity,
    userInfo && 'username' in userInfo ? userInfo.username : ''
  );
  const isTeamAdmin = maybeTeamAdmin ?? false;

  // Check if any chat is loading
  const isAnyLoading = useMemo(
    () => playgroundStates.some(state => state.loading),
    [playgroundStates]
  );

  // Get inference context and check if user has access to W&B Inference
  const inferenceContext = useInferenceContext();
  const {canAccessInference} = useCanAccessInference(inferenceContext);

  // Check if user can access billing
  const {canAccessBilling} = useCanAccessBilling({entityName: entity});

  const llmDropdownOptions = useLLMDropdownOptions(
    configuredProviders,
    configuredProvidersLoading,
    customProvidersResult,
    customProviderModelsResult,
    areCustomProvidersLoading,
    savedModelsResult,
    savedModelsLoading,
    canAccessInference,
    entity,
    canAccessBilling
  );

  // Check if there are any configured providers (excluding coreweave if user can't access inference)
  const hasConfiguredProviders = useMemo(() => {
    if (configuredProvidersLoading) {
      return true;
    } // Don't show empty state while loading

    // Filter out coreweave if user doesn't have inference access
    const availableProviders = canAccessInference
      ? configuredProviders
      : Object.fromEntries(
          Object.entries(configuredProviders).filter(
            ([provider]) => provider !== 'coreweave'
          )
        );

    return Object.values(availableProviders).some(({status}) => status);
  }, [configuredProviders, configuredProvidersLoading, canAccessInference]);

  if (!hasConfiguredProviders && !isOpenInPlayground) {
    return (
      <div className="flex h-full w-full flex-col items-center overflow-hidden">
        <div className="relative m-[8px] flex h-full max-h-[calc(100%-130px)] w-full min-w-[520px] max-w-[800px] flex-1 rounded-[4px] border border-moon-200">
          <div className="relative flex h-full w-full flex-col">
            <div className="absolute top-0 z-[10] w-full bg-white px-[8px] py-[16px]">
              <PlaygroundChatTopBar
                idx={0}
                settingsTab={settingsTab}
                setSettingsTab={setSettingsTab}
                setPlaygroundStateField={setPlaygroundStateField}
                setPlaygroundStates={setPlaygroundStates}
                playgroundStates={playgroundStates}
                entity={entity}
                project={project}
                isTeamAdmin={isTeamAdmin}
                refetchConfiguredProviders={refetchConfiguredProviders}
                refetchCustomLLMs={refetchCustomLLMs}
                llmDropdownOptions={llmDropdownOptions}
                areProvidersLoading={
                  configuredProvidersLoading || areCustomProvidersLoading
                }
                customProvidersResult={customProvidersResult}
              />
            </div>
            <div className="flex h-full w-full items-center justify-center pt-[108px]">
              <EmptyWithSettingsButton
                entity={entity}
                project={project}
                isTeamAdmin={isTeamAdmin}
                onConfigureProvider={() => {
                  refetchConfiguredProviders();
                }}
              />
            </div>
          </div>
        </div>
        <PlaygroundChatInput
          chatText={chatText}
          setChatText={setChatText}
          isLoading={isAnyLoading}
          onSend={handleStreamSend}
          onAdd={handleAddMessage}
          hasConfiguredProviders={hasConfiguredProviders}
          disableSend={disableSendMessage || undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-center overflow-hidden">
      <InferenceBanner />
      <div className="mx-auto flex h-full w-full max-w-none overflow-x-auto overflow-y-hidden">
        <div className="mx-auto flex flex-1 justify-center gap-2">
          {playgroundStates.map((state, idx) => (
            <React.Fragment key={idx}>
              <div
                className={`relative m-[8px] flex w-full min-w-[520px] ${
                  playgroundStates.length === 1 ? 'lg:min-w-[800px] flex-1' : ''
                } max-w-[800px] flex-col rounded-[4px] border ${
                  settingsTab === idx
                    ? 'border-teal-400 outline outline-[1.5px] outline-teal-400'
                    : 'border-moon-200'
                }`}>
                <div className="absolute top-0 z-[10] w-full rounded-t-[4px] bg-white px-[16px] py-[8px]">
                  <PlaygroundChatTopBar
                    idx={idx}
                    settingsTab={settingsTab}
                    setSettingsTab={setSettingsTab}
                    setPlaygroundStateField={setPlaygroundStateField}
                    setPlaygroundStates={setPlaygroundStates}
                    playgroundStates={playgroundStates}
                    entity={entity}
                    project={project}
                    isTeamAdmin={isTeamAdmin}
                    refetchConfiguredProviders={refetchConfiguredProviders}
                    refetchCustomLLMs={refetchCustomLLMs}
                    llmDropdownOptions={llmDropdownOptions}
                    areProvidersLoading={
                      configuredProvidersLoading ||
                      areCustomProvidersLoading ||
                      savedModelsLoading
                    }
                    customProvidersResult={customProvidersResult}
                  />
                </div>
                <div className="h-full w-full flex-grow overflow-auto px-[16px] pt-[108px]">
                  <div className=" mx-auto mt-[32px] pb-8">
                    {state.traceCall && (
                      <PlaygroundContext.Provider
                        value={{
                          isPlayground: true,
                          isStreaming: state.loading,
                          deleteMessage: (messageIndex, responseIndexes) =>
                            deleteMessage(idx, messageIndex, responseIndexes),
                          editMessage: (messageIndex, newMessage) =>
                            editMessage(idx, messageIndex, newMessage),
                          deleteChoice: (messageIndex, choiceIndex) =>
                            deleteChoice(idx, choiceIndex),
                          addMessage: newMessage => addMessage(idx, newMessage),
                          editChoice: (choiceIndex, newChoice) =>
                            editChoice(idx, choiceIndex, newChoice),
                          retry: (messageIndex: number, choiceIndex?: number) =>
                            handleRetryStream(idx, messageIndex, choiceIndex),
                          sendMessage: (
                            role: PlaygroundMessageRole,
                            content: string,
                            toolCallId?: string
                          ) => {
                            handleStreamSend(
                              role,
                              chatText,
                              idx,
                              content,
                              toolCallId
                            );
                          },
                          setSelectedChoiceIndex: (choiceIndex: number) =>
                            setPlaygroundStateField(
                              idx,
                              'selectedChoiceIndex',
                              choiceIndex
                            ),
                          isMarkdownEnabled: showMarkdownInChatView,
                          setIsMarkdownEnabled: setShowMarkdownInChatView,
                          hasMultipleUnfinishedToolCalls: (() => {
                            const messages = state.traceCall?.inputs
                              ?.messages as Message[] | undefined;
                            const choices = (state.traceCall?.output as any)
                              ?.choices as Choice[] | undefined;
                            const count = messages
                              ? checkForUnansweredToolCalls(messages, choices)
                              : 0;
                            return count > 1;
                          })(),
                        }}>
                        <CallChat
                          call={state.traceCall as TraceCallSchema}
                          useDrawerAnimationBuffer={false}
                        />
                      </PlaygroundContext.Provider>
                    )}
                  </div>
                </div>
                <div className="relative mx-auto w-full max-w-[800px] p-[8px] pl-[12px]">
                  {state.traceCall.summary && (
                    <PlaygroundCallStats
                      call={state.traceCall as TraceCallSchema}
                    />
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      <PlaygroundChatInput
        chatText={chatText}
        setChatText={setChatText}
        isLoading={isAnyLoading}
        onSend={handleStreamSend}
        onAdd={handleAddMessage}
        hasConfiguredProviders={hasConfiguredProviders}
        disableSend={disableSendMessage || undefined}
      />
    </div>
  );
};
